"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isManualSeekDelta = exports.timeSavedSeconds = exports.totalKeyPointSeconds = exports.calculateTimelinePosition = exports.segmentColorByImpact = void 0;
exports.timelineSegmentColors = timelineSegmentColors;
exports.skimProgressPercent = skimProgressPercent;
exports.keyPointProgressPercent = keyPointProgressPercent;
exports.findKeyPointIndexAtTime = findKeyPointIndexAtTime;
const utils_1 = require("@repo/utils");
const palette_1 = require("./palette");
const skim_timeline_1 = require("./skim-timeline");
/** Timeline segment colour from the keypoint's impact level. */
const segmentColorByImpact = (impactLevel) => {
    const norm = (impactLevel ?? '').toLowerCase();
    if (norm.includes('high') || norm.includes('major')) {
        return palette_1.ANALYSIS_PALETTE.segment.high;
    }
    if (norm.includes('low') || norm.includes('minor')) {
        return palette_1.ANALYSIS_PALETTE.segment.low;
    }
    return palette_1.ANALYSIS_PALETTE.segment.default;
};
exports.segmentColorByImpact = segmentColorByImpact;
/**
 * One timeline colour per keypoint, index-aligned with `keyPoints`. Shades
 * are handed out in time order rather than list order, so neighbours on the
 * strip always differ — including a segment that starts on the very second
 * the previous one ends.
 */
function timelineSegmentColors(keyPoints) {
    const shades = palette_1.ANALYSIS_PALETTE.segmentCycle;
    const colors = [];
    keyPoints
        .map((keyPoint, index) => ({
        index,
        startSeconds: (0, utils_1.timestampToSeconds)(keyPoint.startTimestamp),
    }))
        .sort((a, b) => a.startSeconds - b.startSeconds)
        .forEach(({ index }, rank) => {
        colors[index] =
            shades[rank % shades.length] ?? palette_1.ANALYSIS_PALETTE.segment.default;
    });
    return colors;
}
const clampPercent = (value) => Math.min(Math.max(value, 0), 100);
const calculateTimelinePosition = (time, duration) => (duration <= 0 ? 0 : clampPercent((time / duration) * 100));
exports.calculateTimelinePosition = calculateTimelinePosition;
const totalKeyPointSeconds = (keyPoints) => keyPoints.reduce((acc, kp) => acc + kp.totalDurationSeconds, 0);
exports.totalKeyPointSeconds = totalKeyPointSeconds;
/** How much of the full video the skim saves; never negative. */
const timeSavedSeconds = (videoDurationSeconds, keyPoints) => Math.max(0, (videoDurationSeconds ?? 0) - (0, exports.totalKeyPointSeconds)(keyPoints));
exports.timeSavedSeconds = timeSavedSeconds;
/**
 * Progress through the whole skim (all keypoint windows laid end to end) at
 * `currentTime`, as a percentage.
 *
 * Measured in timestamp spans throughout — see `skimWindowSeconds` for why
 * `totalDurationSeconds` cannot be mixed in here. A bar drawn from this is
 * read back by `resolveSkimPosition`, which resolves against the timestamps.
 */
function skimProgressPercent(keyPoints, currentTime) {
    const total = (0, skim_timeline_1.totalSkimSeconds)(keyPoints);
    if (total === 0)
        return 0;
    let elapsed = 0;
    for (const kp of keyPoints) {
        const start = (0, utils_1.timestampToSeconds)(kp.startTimestamp);
        const end = (0, utils_1.timestampToSeconds)(kp.endTimestamp);
        if (currentTime < start)
            break;
        if (currentTime <= end) {
            elapsed += currentTime - start;
            break;
        }
        elapsed += (0, skim_timeline_1.skimWindowSeconds)(kp);
    }
    return clampPercent((elapsed / total) * 100);
}
/** Progress through a single keypoint window, as a percentage. */
function keyPointProgressPercent(keyPoint, currentTime) {
    const start = (0, utils_1.timestampToSeconds)(keyPoint.startTimestamp);
    const end = (0, utils_1.timestampToSeconds)(keyPoint.endTimestamp);
    if (currentTime > end)
        return 100;
    if (currentTime < start || end <= start)
        return 0;
    return clampPercent(((currentTime - start) / (end - start)) * 100);
}
/**
 * The 250ms skim poll advances ~0.25s per tick, so a jump of more than 2s
 * forward, or any jump back past 0.5s, means the user dragged the slider.
 */
const isManualSeekDelta = (deltaSeconds) => deltaSeconds > 2 || deltaSeconds < -0.5;
exports.isManualSeekDelta = isManualSeekDelta;
/** Index of the keypoint whose window contains `timeSeconds`, or -1. */
function findKeyPointIndexAtTime(keyPoints, timeSeconds, excludeIndex = -1) {
    return keyPoints.findIndex((kp, i) => {
        if (i === excludeIndex)
            return false;
        const start = (0, utils_1.timestampToSeconds)(kp.startTimestamp);
        const end = (0, utils_1.timestampToSeconds)(kp.endTimestamp);
        return timeSeconds >= start && timeSeconds <= end;
    });
}
